import React from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.css';

/**
 * DatePicker — a calendar-first date input.
 *
 * Wraps react-datepicker so the month grid is visible while choosing, which a bare
 * <input type="date"> leaves to the browser. Weekend days are shaded and italicised
 * but remain selectable: the point is to make them easy to avoid, not to forbid a
 * deliberate weekend date.
 *
 * The library's own stylesheet is imported here and re-skinned in DatePicker.css
 * against the semantic tokens, so the calendar is legible in dark mode too.
 *
 * Value in / value out is an ISO `YYYY-MM-DD` string, matching the `type="date"`
 * inputs it replaces, so a caller's form state and payloads need no changes.
 */

// Bangladesh works Sunday–Thursday; the weekend is Friday and Saturday.
// getDay(): 0 Sun, 1 Mon, 2 Tue, 3 Wed, 4 Thu, 5 Fri, 6 Sat.
export const WEEKEND_DAYS = [5, 6];

export const isWeekend = (date) => !!date && WEEKEND_DAYS.includes(date.getDay());

/**
 * Parse `YYYY-MM-DD` as a LOCAL date. `new Date('2026-08-04')` is parsed as UTC and
 * lands on the previous day for anyone west of UTC, which would show and submit the
 * wrong day.
 */
export const parseIsoDate = (value) => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) {
    const loose = new Date(value);
    return Number.isNaN(loose.getTime()) ? null : loose;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

/** Format a Date back to `YYYY-MM-DD` using its local parts, for the same reason. */
export const toIsoDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const DatePicker = ({
  value,
  onChange,
  id,
  disabled = false,
  placeholder = 'Select a date',
  minDate,
  maxDate,
  showLegend = true,
  className = '',
  ...rest
}) => (
  <div className={`bib-datepicker-wrapper ${className}`.trim()}>
    <ReactDatePicker
      id={id}
      selected={parseIsoDate(value)}
      onChange={(date) => onChange(toIsoDate(date))}
      dateFormat="dd MMM yyyy"
      placeholderText={placeholder}
      disabled={disabled}
      minDate={parseIsoDate(minDate) || undefined}
      maxDate={parseIsoDate(maxDate) || undefined}
      calendarClassName="bib-datepicker"
      className="form-control form-control-sm"
      // Marks weekends for DatePicker.css without disabling them.
      dayClassName={(date) => (isWeekend(date) ? 'react-datepicker__day--weekend' : undefined)}
      showPopperArrow={false}
      // Keep the calendar above react-bootstrap modals, which sit at z-index 1055.
      popperProps={{ strategy: 'fixed' }}
      {...rest}
    >
      {showLegend && (
        <div className="bib-datepicker-legend">
          <span className="swatch" aria-hidden="true" />
          Shaded days are the weekend (Fri&ndash;Sat) &mdash; selectable, but usually avoided
        </div>
      )}
    </ReactDatePicker>
  </div>
);

export default DatePicker;
